import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Store, Plus, MapPin, Phone, Building2, CheckCircle2, Loader, Navigation } from 'lucide-react';
import Swal from 'sweetalert2';
import UseAxiosSecure from '../hooks/UseAxiosSecure';
import UseAuth from '../hooks/UseAuth';

const BranchManager = () => {
    const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isGettingLocation, setIsGettingLocation] = useState(false);
    
    const axiosSecure = UseAxiosSecure();
    const queryClient = useQueryClient();
    const { activeBranchId } = UseAuth();

    // FETCH ALL BRANCHES
    const { data: branches = [], isLoading } = useQuery({
        queryKey: ['branches-list'], 
        queryFn: async () => {
            const res = await axiosSecure.get('/branches');
            return res.data?.data || [];
        }
    });

    // Helper to auto-fill browser geolocation
    const handleGetLocation = () => {
        if (!navigator.geolocation) {
            Swal.fire({ title: "Error", text: "Geolocation is not supported by your browser", icon: "error" });
            return;
        }
        setIsGettingLocation(true);
        navigator.geolocation.getCurrentPosition(
            (position) => {
                setValue("latitude", position.coords.latitude, { shouldValidate: true });
                setValue("longitude", position.coords.longitude, { shouldValidate: true });
                setIsGettingLocation(false);
            },
            (error) => {
                setIsGettingLocation(false);
                Swal.fire({ title: "Location Error", text: error.message, icon: "error" });
            }
        );
    };

    // CREATE NEW BRANCH
    const onSubmit = async (data) => {
        setIsSubmitting(true);
        try {
            // Ensure numbers are sent as float values
            const payload = {
                ...data,
                latitude: parseFloat(data.latitude),
                longitude: parseFloat(data.longitude)
            };

            const res = await axiosSecure.post('/branch/create', payload);
            
            if (res.data.success) {
                await queryClient.invalidateQueries(['branches-list']);
                
                Swal.fire({
                    title: "Branch Created!",
                    text: `${data.name} has been added to your network.`,
                    icon: "success",
                    background: '#1a103c',
                    color: '#fff',
                    confirmButtonColor: "#db2777"
                });
                reset();
            }
        } catch (error) {
            Swal.fire({
                title: "Error",
                text: error.response?.data?.message || "Failed to create branch",
                icon: "error",
                background: '#1a103c',
                color: '#fff'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="max-w-7xl mx-auto space-y-6">
            
            {/* HEADER */}
            <div className="bg-[#1a103c]/40 backdrop-blur-md p-6 rounded-3xl border border-white/5">
                <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                    <Building2 className="text-pink-500" /> Organization Settings
                </h2>
                <p className="text-slate-400 text-sm mt-1">Manage your restaurant locations and expand your franchise.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                
                {/* LEFT: ADD NEW BRANCH FORM */}
                <div className="lg:col-span-1 space-y-6">
                    <div className="bg-[#1a103c]/60 backdrop-blur-xl border border-white/5 rounded-3xl p-8 shadow-xl">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-lg font-bold text-white flex items-center gap-2">
                                <Plus size={20} className="text-emerald-500"/> Add New Location
                            </h3>
                            <button
                                type="button"
                                onClick={handleGetLocation}
                                disabled={isGettingLocation}
                                className="text-xs flex items-center gap-1 bg-white/10 hover:bg-white/20 text-pink-400 py-1.5 px-3 rounded-lg border border-pink-500/20 transition-all"
                            >
                                <Navigation size={12} className={isGettingLocation ? 'animate-spin' : ''} />
                                {isGettingLocation ? 'Getting GPS...' : 'Use My GPS'}
                            </button>
                        </div>
                        
                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                            {/* Branch Name */}
                            <div className="space-y-2">
                                <label className="text-sm font-bold text-slate-300 ml-1">Branch Name</label>
                                <div className="relative group">
                                    <Store className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-pink-500 transition-colors" size={18} />
                                    <input 
                                        {...register("name", { required: "Branch Name is required" })}
                                        type="text" 
                                        placeholder="e.g. Jilapi - Downtown" 
                                        className="w-full bg-[#0f0a1f] border border-white/10 rounded-xl py-3 pl-12 pr-4 text-white focus:border-pink-500/50 focus:outline-none transition-all"
                                    />
                                </div>
                                {errors.name && <span className="text-red-400 text-xs ml-2">{errors.name.message}</span>}
                            </div>

                            {/* Phone Number */}
                            <div className="space-y-2">
                                <label className="text-sm font-bold text-slate-300 ml-1">Contact Phone</label>
                                <div className="relative group">
                                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-cyan-500 transition-colors" size={18} />
                                    <input 
                                        {...register("phone", { required: "Phone number is required" })}
                                        type="text" 
                                        placeholder="e.g. +880 1700 000000" 
                                        className="w-full bg-[#0f0a1f] border border-white/10 rounded-xl py-3 pl-12 pr-4 text-white focus:border-cyan-500/50 focus:outline-none transition-all"
                                    />
                                </div>
                                {errors.phone && <span className="text-red-400 text-xs ml-2">{errors.phone.message}</span>}
                            </div>

                            {/* Address */}
                            <div className="space-y-2">
                                <label className="text-sm font-bold text-slate-300 ml-1">Full Address</label>
                                <div className="relative group">
                                    <MapPin className="absolute left-4 top-4 text-slate-500 group-focus-within:text-purple-500 transition-colors" size={18} />
                                    <textarea 
                                        {...register("address", { required: "Address is required" })}
                                        rows="3"
                                        placeholder="Enter the full physical address..." 
                                        className="w-full bg-[#0f0a1f] border border-white/10 rounded-xl py-3 pl-12 pr-4 text-white focus:border-purple-500/50 focus:outline-none transition-all resize-none"
                                    ></textarea>
                                </div>
                                {errors.address && <span className="text-red-400 text-xs ml-2">{errors.address.message}</span>}
                            </div>

                            {/* Latitude & Longitude Inputs */}
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <label className="text-sm font-bold text-slate-300 ml-1">Latitude</label>
                                    <input 
                                        {...register("latitude", { required: "Latitude is required" })}
                                        type="number"
                                        step="any"
                                        placeholder="23.8103" 
                                        className="w-full bg-[#0f0a1f] border border-white/10 rounded-xl py-3 px-4 text-white focus:border-pink-500/50 focus:outline-none transition-all text-sm"
                                    />
                                    {errors.latitude && <span className="text-red-400 text-xs ml-1">{errors.latitude.message}</span>}
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-bold text-slate-300 ml-1">Longitude</label>
                                    <input 
                                        {...register("longitude", { required: "Longitude is required" })}
                                        type="number"
                                        step="any"
                                        placeholder="90.4125" 
                                        className="w-full bg-[#0f0a1f] border border-white/10 rounded-xl py-3 px-4 text-white focus:border-pink-500/50 focus:outline-none transition-all text-sm"
                                    />
                                    {errors.longitude && <span className="text-red-400 text-xs ml-1">{errors.longitude.message}</span>}
                                </div>
                            </div>

                            <button 
                                disabled={isSubmitting}
                                type="submit" 
                                className="w-full py-4 rounded-xl bg-gradient-to-r from-pink-600 to-purple-600 text-white font-bold text-lg shadow-lg shadow-pink-500/20 hover:shadow-pink-500/40 hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:grayscale"
                            >
                                {isSubmitting ? <Loader className="animate-spin" /> : <><Store size={20} /> Create Branch</>}
                            </button>
                        </form>
                    </div>
                </div>

                {/* RIGHT: BRANCHES LIST */}
                <div className="lg:col-span-2">
                    <div className="bg-[#1a103c]/60 backdrop-blur-xl border border-white/5 rounded-3xl p-8 shadow-xl min-h-full">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-lg font-bold text-white flex items-center gap-2">
                                <MapPin size={20} className="text-pink-500"/> Active Locations
                            </h3>
                            <span className="px-3 py-1 bg-white/5 rounded-lg text-slate-400 text-sm font-bold border border-white/10">
                                {branches.length} Total
                            </span>
                        </div>

                        {isLoading ? (
                            <div className="flex justify-center items-center h-64">
                                <Loader className="animate-spin text-pink-500" size={40} />
                            </div>
                        ) : branches.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                                <Store size={48} className="mb-4 opacity-20" />
                                <p>No branches found.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {branches.map((branch) => {
                                    const isActive = activeBranchId === branch._id;
                                    
                                    return (
                                        <div 
                                            key={branch._id} 
                                            className={`relative p-6 rounded-2xl border transition-all duration-300 ${
                                                isActive 
                                                    ? 'bg-gradient-to-br from-pink-500/10 to-purple-600/10 border-pink-500/30 shadow-[0_0_15px_rgba(236,72,153,0.1)]' 
                                                    : 'bg-[#0f0a1f]/50 border-white/5 hover:border-white/20'
                                            }`}
                                        >
                                            {isActive && (
                                                <div className="absolute top-4 right-4 text-pink-500 flex items-center gap-1 text-xs font-bold uppercase tracking-wider">
                                                    <CheckCircle2 size={14} /> Currently Viewing
                                                </div>
                                            )}
                                            
                                            <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mb-4">
                                                <Store size={24} className={isActive ? 'text-pink-400' : 'text-slate-400'} />
                                            </div>
                                            
                                            <h4 className="text-xl font-bold text-white mb-2">{branch.name}</h4>
                                            
                                            <div className="space-y-2 text-sm text-slate-400">
                                                <div className="flex items-start gap-2">
                                                    <MapPin size={16} className="shrink-0 mt-0.5" />
                                                    <span className="line-clamp-2">{branch.address}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Phone size={16} className="shrink-0" />
                                                    <span>{branch.phone}</span>
                                                </div>
                                                {(branch.latitude && branch.longitude) && (
                                                    <div className="text-xs text-slate-500 font-mono">
                                                        GPS: {branch.latitude.toFixed(4)}, {branch.longitude.toFixed(4)}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
                
            </div>
        </div>
    );
};

export default BranchManager;